import { Theme } from '@emotion/react';
import { FormControl, Grid, SxProps } from '@mui/material';
import { DatePicker, DateView, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { Control, Controller, FieldError } from 'react-hook-form';
import { esES } from '@mui/x-date-pickers/locales';
import { gridSize } from '@/shared/constants';
import { useState } from 'react';

export interface SampleDatePickerProps {
  label: string;
  onChangeValue?: (e?: any) => void;
  onChangeRawValue?: (e?: any) => void;

  size?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };

  control: Control<any, any>;
  name: string;
  defaultValue: string | null;
  error: FieldError | undefined;
  helperText: React.ReactNode;

  textFieldKey?: string;
  minDate?: Date | string | Dayjs | null;
  maxDate?: Date | string | Dayjs | null;

  display?: string;

  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;

  sxTextField?: SxProps<Theme>;
  clearable?: boolean;
  format?: string;
  views?: DateView[]

  shouldDisableDate?: (day: Dayjs | null) => boolean;
}

const SampleDatePicker: React.FC<SampleDatePickerProps> = ({
  label,
  name,
  onChangeValue,
  onChangeRawValue,
  control,
  defaultValue,
  size = gridSize,
  textFieldKey,
  minDate,
  maxDate,
  error,
  helperText,
  display = 'flex',
  required = true,
  disabled = false,
  readonly = false,
  sxTextField,
  clearable = false,

  shouldDisableDate,

  format = 'YYYY-MM-DD',
  views = ['year', 'month', 'day']
}) => {

  const [open, setOpen] = useState(false);


  return (
    <Grid
      item
      {...size}
      sx={{
        display: display,
      }}
    >
      <FormControl fullWidth>
        <Controller
          control={control}
          key={textFieldKey ?? defaultValue}
          name={name}
          defaultValue={defaultValue ?? ''}
          render={({ field }) => {
            const onChange = (date: any) => {
              field.onChange(dayjs(date).format('YYYY-MM-DD'));
              onChangeValue && onChangeValue(dayjs(date).format('YYYY-MM-DD'));
              onChangeRawValue && onChangeRawValue(date);
            };

            return (
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="es"
                localeText={
                  esES.components.MuiLocalizationProvider.defaultProps
                    .localeText
                }
              >
                <DatePicker
                  {...field}
                  label={label}
                  format={format}
                  views={views}
                  open={open}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  slotProps={{
                    textField: {
                      // size: 'small',
                      helperText: helperText,
                      error: !!error,
                      required: required,
                      inputProps: {
                        readOnly: true,
                      },
                      onClick: () => { setOpen(true) },

                      sx: {
                        ...sxTextField,
                        '.css-1565pkx-MuiFormLabel-root-MuiInputLabel-root.Mui-disabled': {
                          color: '#6C737F'
                        },
                        '.css-1u7ltmc-MuiInputBase-input-MuiFilledInput-input.Mui-disabled': {
                          WebkitTextFillColor: '#111927',
                          color: '#6C737F'
                        },
                        '& .MuiInputBase-root.Mui-disabled': {
                          backgroundColor: '#8080800d',
                        },
                      },
                    },
                    field: {
                      clearable: clearable,
                    },
                  }}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={onChange}
                  minDate={minDate ? dayjs(minDate) : null}
                  maxDate={maxDate ? dayjs(maxDate) : null}
                  disabled={disabled}
                  readOnly={readonly}
                  shouldDisableDate={
                    shouldDisableDate
                      ? day => shouldDisableDate(day)
                      : undefined
                  }
                />
              </LocalizationProvider>
            );
          }}
        />
      </FormControl>
    </Grid>
  );
};

export default SampleDatePicker;
